import React, { useEffect, useState, useRef } from 'react';
import {
  MessageSquare,
  ShieldCheck,
  Target,
  GitBranch,
  Workflow,
  FileText,
  Database,
  BookOpen,
  Sliders,
  Play,
  CheckCircle2,
  Loader2,
  Sparkles,
  X,
  Bot,
  BrainCircuit,
  Zap,
} from 'lucide-react';
import type { Theme, ThemeColors } from './ChatbotIntentsListing';
import type { IntentClassification } from './intentDefinitionPayload';

type StepStatus = 'pending' | 'running' | 'complete';

export interface SimulationContext {
  query: string;
  intentCode: string;
  intentName: string;
  classification: IntentClassification;
  classificationLabel: string;
  confidence: number;
  orchestration?: { id: string; name: string; parameters: string[] };
  report?: { id: string; name: string; parameters: string[] };
  dataModel?: { id: string; name: string; entities: string[] };
  knowledgeBases?: { id: string; name: string }[];
}

interface SimulationStep {
  id: string;
  title: string;
  detail: string;
  thinkingLines: string[];
  icon: React.ReactNode;
  durationMs: number;
  status: StepStatus;
}

const SIM_UI = {
  dark: {
    backdrop: 'bg-black/70',
    panel: 'bg-neutral-900 border-neutral-700',
    headerIcon: 'bg-violet-950/50 border-violet-800/50 text-violet-300',
    aiBar: 'bg-violet-950/30 border-violet-800/40',
    aiBarShimmer: 'from-transparent via-violet-400/20 to-transparent',
    stepPending: 'border-neutral-700/80 bg-neutral-900/40 opacity-45',
    stepRunning: 'border-violet-500/50 bg-violet-950/20 ring-1 ring-violet-500/30',
    stepComplete: 'border-neutral-700 bg-neutral-800/30',
    connector: 'bg-neutral-700',
    connectorActive: 'bg-violet-500',
    iconPending: 'text-neutral-500 bg-neutral-800 border-neutral-700',
    iconRunning: 'text-violet-200 bg-violet-900/60 border-violet-600 sim-ai-glow',
    iconComplete: 'text-emerald-400 bg-emerald-950/40 border-emerald-700/50',
    progressTrack: 'bg-neutral-800',
    progressFill: 'bg-gradient-to-r from-violet-500 via-indigo-500 to-emerald-500',
    resultBox: 'border-emerald-700/40 bg-emerald-950/20',
    queryChip: 'bg-neutral-800 border-neutral-700 text-neutral-200',
    thinkingLog: 'bg-neutral-950/80 border-neutral-700 text-violet-200/90',
    chatBubble: 'bg-neutral-800 border-neutral-700',
  },
  light: {
    backdrop: 'bg-slate-900/35',
    panel: 'bg-white border-slate-200',
    headerIcon: 'bg-violet-50 border-violet-100 text-violet-600',
    aiBar: 'bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-100',
    aiBarShimmer: 'from-transparent via-violet-300/30 to-transparent',
    stepPending: 'border-slate-100 bg-slate-50/60 opacity-50',
    stepRunning: 'border-violet-200 bg-violet-50/50 ring-1 ring-violet-200',
    stepComplete: 'border-slate-200 bg-white',
    connector: 'bg-slate-200',
    connectorActive: 'bg-violet-400',
    iconPending: 'text-slate-400 bg-slate-100 border-slate-200',
    iconRunning: 'text-violet-600 bg-violet-100 border-violet-200 sim-ai-glow',
    iconComplete: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    progressTrack: 'bg-slate-100',
    progressFill: 'bg-gradient-to-r from-violet-500 via-indigo-500 to-emerald-500',
    resultBox: 'border-emerald-200 bg-emerald-50/60',
    queryChip: 'bg-slate-50 border-slate-200 text-slate-700',
    thinkingLog: 'bg-slate-900 border-slate-800 text-emerald-400/90',
    chatBubble: 'bg-slate-50 border-slate-200',
  },
} as const;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildSteps(ctx: SimulationContext): Omit<SimulationStep, 'status'>[] {
  const queryPreview = ctx.query.trim() || 'show records created this month';
  const confidencePct = `${(ctx.confidence * 100).toFixed(0)}%`;

  const pipeline: Omit<SimulationStep, 'status'>[] = [
    {
      id: 'query',
      title: 'Reading user message',
      detail: `Input: "${queryPreview.length > 80 ? `${queryPreview.slice(0, 80)}…` : queryPreview}"`,
      thinkingLines: ['Tokenizing utterance…', 'Normalizing entities & date references…'],
      icon: <MessageSquare className="w-4 h-4" />,
      durationMs: 700,
    },
    {
      id: 'policy',
      title: 'Running policy guardrails',
      detail: 'Content policy, tenant scope, and RBAC checks passed.',
      thinkingLines: ['Scanning for PII exposure…', 'Verifying tenant isolation rules…', 'Policy engine: ALLOW'],
      icon: <ShieldCheck className="w-4 h-4" />,
      durationMs: 850,
    },
    {
      id: 'intent',
      title: 'Detecting intent',
      detail: `Best match → "${ctx.intentName}" (${ctx.intentCode}) · ${confidencePct} confidence`,
      thinkingLines: [
        'Embedding query against intent catalog…',
        `Scoring ${ctx.intentCode} above threshold…`,
        'Cross-checking training phrase overlap…',
      ],
      icon: <BrainCircuit className="w-4 h-4" />,
      durationMs: 950,
    },
    {
      id: 'route',
      title: 'Reasoning about route',
      detail: `Routing decision: ${ctx.classificationLabel}`,
      thinkingLines: ['Evaluating classification signals…', 'Selecting downstream handler…'],
      icon: <GitBranch className="w-4 h-4" />,
      durationMs: 750,
    },
  ];

  if (ctx.classification === 'requesting_action' && ctx.orchestration) {
    pipeline.push(
      {
        id: 'orch-match',
        title: 'Matching orchestration',
        detail: `Workflow "${ctx.orchestration.name}" (${ctx.orchestration.id})`,
        thinkingLines: ['Comparing keyword embeddings…', 'Workflow trigger confidence: 0.94'],
        icon: <Workflow className="w-4 h-4" />,
        durationMs: 900,
      },
      {
        id: 'params',
        title: 'Extracting parameters',
        detail:
          ctx.orchestration.parameters.length > 0
            ? `Slots: ${ctx.orchestration.parameters.join(', ')}`
            : 'No slots required — proceeding with defaults.',
        thinkingLines: ['Running NER on user utterance…', 'Validating slot completeness…'],
        icon: <Sliders className="w-4 h-4" />,
        durationMs: 1000,
      },
      {
        id: 'execute',
        title: 'Executing workflow',
        detail: `Dispatching ${ctx.orchestration.id} to orchestration runtime.`,
        thinkingLines: ['Publishing execution event…', 'Awaiting workflow ACK…'],
        icon: <Zap className="w-4 h-4" />,
        durationMs: 1100,
      }
    );
  } else if (ctx.classification === 'requesting_report' || ctx.classification === 'live_report') {
    if (ctx.classification === 'requesting_report' && ctx.report) {
      pipeline.push(
        {
          id: 'report-match',
          title: 'Resolving report catalog',
          detail: `"${ctx.report.name}" (${ctx.report.id})`,
          thinkingLines: ['Searching Analytics Hub index…', 'Report similarity score: 0.91'],
          icon: <FileText className="w-4 h-4" />,
          durationMs: 900,
        },
        {
          id: 'params',
          title: 'Mapping report filters',
          detail:
            ctx.report.parameters.length > 0
              ? `Filters: ${ctx.report.parameters.join(', ')}`
              : 'Applying default report scope.',
          thinkingLines: ['Parsing temporal expressions…', 'Binding filter parameters…'],
          icon: <Sliders className="w-4 h-4" />,
          durationMs: 950,
        }
      );
      pipeline.push({
        id: 'execute',
        title: 'Generating report',
        detail: `Streaming ${ctx.report.id} output to conversation layer.`,
        thinkingLines: ['Queuing report job…', 'Materializing result set…'],
        icon: <Play className="w-4 h-4" />,
        durationMs: 1150,
      });
    } else if (ctx.classification === 'live_report' && ctx.dataModel) {
      pipeline.push(
        {
          id: 'model',
          title: 'Selecting live data model',
          detail: `${ctx.dataModel.name} (${ctx.dataModel.id})`,
          thinkingLines: ['Resolving schema metadata…', `Entities: ${ctx.dataModel.entities.join(', ')}`],
          icon: <Database className="w-4 h-4" />,
          durationMs: 850,
        },
        {
          id: 'query-plan',
          title: 'Building query plan',
          detail: 'Composing live SQL/API aggregation plan.',
          thinkingLines: ['Optimizing join path…', 'Estimating row cardinality…'],
          icon: <Sliders className="w-4 h-4" />,
          durationMs: 1000,
        },
        {
          id: 'execute',
          title: 'Running live query',
          detail: 'Executing aggregation · TTL cache 60s.',
          thinkingLines: ['Query dispatched to data vault…', 'First rows received…'],
          icon: <Zap className="w-4 h-4" />,
          durationMs: 1200,
        }
      );
    }
  } else if (ctx.classification === 'general_search') {
    const kbNames = ctx.knowledgeBases?.map((k) => k.name).join(', ') || 'default corpus';
    pipeline.push(
      {
        id: 'kb',
        title: 'Retrieving knowledge',
        detail: `Corpus: ${kbNames}`,
        thinkingLines: ['Hybrid search: BM25 + vector…', 'Retrieved 12 candidate passages'],
        icon: <BookOpen className="w-4 h-4" />,
        durationMs: 950,
      },
      {
        id: 'rank',
        title: 'Ranking context',
        detail: 'Top-3 passages selected for grounding.',
        thinkingLines: ['Re-ranking with cross-encoder…', 'Context window assembled'],
        icon: <Target className="w-4 h-4" />,
        durationMs: 850,
      },
      {
        id: 'execute',
        title: 'Synthesizing answer',
        detail: 'Generating grounded response from retrieved excerpts.',
        thinkingLines: ['LLM inference started…', 'Citing source passages…'],
        icon: <Sparkles className="w-4 h-4" />,
        durationMs: 1100,
      }
    );
  }

  pipeline.push({
    id: 'done',
    title: 'Complete',
    detail: 'Pipeline finished — response ready.',
    thinkingLines: ['Finalizing routing payload…'],
    icon: <CheckCircle2 className="w-4 h-4" />,
    durationMs: 500,
  });

  return pipeline;
}

export function getResultSummary(ctx: SimulationContext): string {
  if (ctx.classification === 'requesting_action' && ctx.orchestration) {
    return `Routed to orchestration ${ctx.orchestration.id}`;
  }
  if (ctx.classification === 'requesting_report') {
    if (ctx.report) {
      return `Report ${ctx.report.id} queued for delivery`;
    }
  }
  if (ctx.classification === 'live_report' && ctx.dataModel) {
    return `Live query executed on ${ctx.dataModel.id}`;
  }
  if (ctx.classification === 'general_search') {
    return `Answer synthesized from ${ctx.knowledgeBases?.length ?? 0} knowledge source(s)`;
  }
  return 'Analysis complete';
}

function getAiResponse(ctx: SimulationContext): string {
  const q = ctx.query.trim() || 'show records created this month';
  if (ctx.classification === 'requesting_action' && ctx.orchestration) {
    return `I'll run the "${ctx.orchestration.name}" workflow for your request: "${q}". Required inputs have been extracted and the orchestration is now executing.`;
  }
  if (ctx.classification === 'requesting_report' && ctx.report) {
    return `I've matched your request to "${ctx.report.name}" and am generating the report now based on: "${q}".`;
  }
  if (ctx.classification === 'live_report' && ctx.dataModel) {
    return `Running a live query against ${ctx.dataModel.name} for: "${q}". Results will appear momentarily.`;
  }
  if (ctx.classification === 'general_search') {
    return `Based on your question — "${q}" — I've retrieved relevant policy documents and composed a grounded answer.`;
  }
  return `Processed: "${q}"`;
}

/** Typewriter + streaming thinking lines for the active step */
const AiStepContent: React.FC<{
  step: SimulationStep;
  colors: ThemeColors;
  sim: (typeof SIM_UI)[Theme];
}> = ({ step, colors, sim }) => {
  const [typedDetail, setTypedDetail] = useState('');
  const [visibleLines, setVisibleLines] = useState<string[]>([]);

  useEffect(() => {
    if (step.status !== 'running') {
      setTypedDetail(step.status === 'complete' ? step.detail : '');
      setVisibleLines(step.status === 'complete' ? step.thinkingLines : []);
      return;
    }

    setTypedDetail('');
    setVisibleLines([]);
    let charIdx = 0;
    let lineIdx = 0;
    let cancelled = false;

    const typeInterval = setInterval(() => {
      if (cancelled) return;
      charIdx++;
      setTypedDetail(step.detail.slice(0, charIdx));
      if (charIdx >= step.detail.length) clearInterval(typeInterval);
    }, 18);

    const lineTimer = setInterval(() => {
      if (cancelled || lineIdx >= step.thinkingLines.length) {
        clearInterval(lineTimer);
        return;
      }
      setVisibleLines((prev) => [...prev, step.thinkingLines[lineIdx]]);
      lineIdx++;
    }, 320);

    return () => {
      cancelled = true;
      clearInterval(typeInterval);
      clearInterval(lineTimer);
    };
  }, [step.id, step.status, step.detail, step.thinkingLines]);

  return (
    <>
      <p className={`text-[11px] mt-1 leading-relaxed ${colors.text.secondary}`}>
        {step.status === 'running' ? (
          <>
            {typedDetail}
            <span className="sim-cursor inline-block w-[2px] h-3 bg-violet-500 ml-0.5 align-middle" />
          </>
        ) : (
          step.detail
        )}
      </p>
      {(step.status === 'running' || step.status === 'complete') && visibleLines.length > 0 && (
        <div className={`mt-2 rounded-md border px-2.5 py-2 font-mono text-[10px] leading-relaxed space-y-0.5 ${sim.thinkingLog}`}>
          {visibleLines.map((line, i) => (
            <div key={i} className="sim-log-line flex items-start gap-1.5" style={{ animationDelay: `${i * 80}ms` }}>
              <span className="text-violet-400/70 shrink-0">›</span>
              <span>{line}</span>
            </div>
          ))}
          {step.status === 'running' && (
            <div className="flex items-center gap-1.5 text-violet-400/60 pt-0.5">
              <span>›</span>
              <span className="sim-thinking-dots">thinking</span>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export interface IntentAnalysisSimulationProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (summary: string) => void;
  theme: Theme;
  colors: ThemeColors;
  runToken: number;
  context: SimulationContext | null;
}

const IntentAnalysisSimulation: React.FC<IntentAnalysisSimulationProps> = ({
  open,
  onClose,
  onComplete,
  theme,
  colors,
  runToken,
  context,
}) => {
  const sim = SIM_UI[theme];
  const [steps, setSteps] = useState<SimulationStep[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [visible, setVisible] = useState(false);
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      const t = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(t);
    }
    setVisible(false);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open || !context || runToken === 0) return;

    let cancelled = false;
    const blueprint = buildSteps(context).map((s) => ({ ...s, status: 'pending' as StepStatus }));

    setSteps(blueprint);
    setIsRunning(true);
    setFinished(false);
    setActiveStepId(null);

    const run = async () => {
      for (let i = 0; i < blueprint.length; i++) {
        if (cancelled) return;
        setActiveStepId(blueprint[i].id);
        setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, status: 'running' } : s)));
        await delay(blueprint[i].durationMs);
        if (cancelled) return;
        setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, status: 'complete' } : s)));
      }
      if (!cancelled) {
        setActiveStepId(null);
        setIsRunning(false);
        setFinished(true);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, runToken, context]);

  useEffect(() => {
    if (finished && context && onComplete) {
      onComplete(getResultSummary(context));
    }
  }, [finished, context, onComplete]);

  useEffect(() => {
    if (scrollRef.current && activeStepId) {
      const el = scrollRef.current.querySelector(`[data-step="${activeStepId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeStepId, steps]);

  if (!open || !context) return null;

  const completedCount = steps.filter((s) => s.status === 'complete').length;
  const progress = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;
  const activeStep = steps.find((s) => s.status === 'running');

  const stepShell = (status: StepStatus) => {
    if (status === 'running') return sim.stepRunning;
    if (status === 'complete') return sim.stepComplete;
    return sim.stepPending;
  };

  const iconShell = (status: StepStatus) => {
    if (status === 'running') return sim.iconRunning;
    if (status === 'complete') return sim.iconComplete;
    return sim.iconPending;
  };

  const queryPreview = context.query.trim() || 'show records created this month';

  return (
    <>
      <style>{`
        @keyframes simStepIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes simAiGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.25); }
          50% { box-shadow: 0 0 0 8px rgba(139, 92, 246, 0); }
        }
        @keyframes simShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes simCursor {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes simLogIn {
          from { opacity: 0; transform: translateX(-4px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes simDotPulse {
          0%, 80%, 100% { opacity: 0.2; }
          40% { opacity: 1; }
        }
        .sim-step-in { animation: simStepIn 0.4s ease-out forwards; }
        .sim-ai-glow { animation: simAiGlow 1.6s ease-in-out infinite; }
        .sim-cursor { animation: simCursor 0.8s step-end infinite; }
        .sim-log-line { animation: simLogIn 0.25s ease-out forwards; }
        .sim-shimmer-bar { animation: simShimmer 1.8s ease-in-out infinite; }
        .sim-thinking-dots::after {
          content: '';
          animation: simDotPulse 1.4s infinite;
        }
      `}</style>

      <div
        className={`fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md transition-opacity duration-300 ${sim.backdrop} ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        role="presentation"
      >
        <div
          className={`w-full max-w-xl rounded-2xl border shadow-2xl flex flex-col max-h-[92vh] transition-all duration-300 ease-out ${sim.panel} ${
            visible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
          }`}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sim-modal-title"
        >
          {/* Header */}
          <div className={`relative overflow-hidden px-5 py-4 border-b ${colors.border} shrink-0`}>
            {isRunning && (
              <div className={`absolute inset-0 overflow-hidden pointer-events-none`}>
                <div className={`absolute inset-y-0 w-1/2 ${sim.aiBarShimmer} sim-shimmer-bar`} />
              </div>
            )}
            <div className="relative flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${sim.headerIcon}`}
                >
                  <Bot className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h2 id="sim-modal-title" className={`text-sm font-semibold ${colors.text.primary}`}>
                    AI Intent Router
                  </h2>
                  <p className={`text-[11px] ${colors.text.muted} mt-0.5 flex items-center gap-1.5`}>
                    {isRunning ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin text-violet-500" />
                        <span>
                          Thinking
                          <span className="sim-thinking-dots">…</span>
                        </span>
                      </>
                    ) : finished ? (
                      <span className="text-emerald-600 font-medium">Analysis complete</span>
                    ) : (
                      'Preparing pipeline…'
                    )}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className={`p-1.5 rounded-lg shrink-0 ${colors.hover} ${colors.text.muted}`}
                aria-label="Close simulation"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* AI status bar */}
          <div className={`mx-5 mt-3 px-3 py-2.5 rounded-xl border text-[11px] shrink-0 ${sim.aiBar}`}>
            <div className="flex items-center gap-2">
              <Sparkles className={`w-3.5 h-3.5 shrink-0 ${isRunning ? 'text-violet-500 animate-pulse' : 'text-slate-400'}`} />
              <span className={colors.text.secondary}>
                {activeStep ? (
                  <>
                    <span className={`font-medium ${colors.text.primary}`}>{activeStep.title}</span>
                    <span className={colors.text.muted}> — processing step {completedCount + 1} of {steps.length}</span>
                  </>
                ) : finished ? (
                  'All reasoning steps completed successfully.'
                ) : (
                  'Initializing AI routing engine…'
                )}
              </span>
            </div>
          </div>

          {/* Query + progress */}
          <div className={`px-5 py-3 shrink-0 space-y-2.5`}>
            <div className={`text-[11px] px-3 py-2 rounded-xl border ${sim.queryChip}`}>
              <span className="text-[10px] uppercase tracking-wide font-semibold text-violet-500/80">You · </span>
              {queryPreview}
            </div>
            <div className="flex items-center gap-2.5">
              <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${sim.progressTrack}`}>
                <div
                  className={`h-full rounded-full transition-all duration-700 ease-out ${sim.progressFill}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className={`text-[10px] font-mono tabular-nums w-8 text-right ${colors.text.muted}`}>
                {progress}%
              </span>
            </div>
          </div>

          {/* Steps */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 pb-4 min-h-0">
            <div className="relative space-y-0">
              {steps.map((step, index) => (
                <div
                  key={`${step.id}-${runToken}`}
                  data-step={step.id}
                  className={`relative flex gap-3 pb-3 last:pb-0 ${
                    step.status !== 'pending' ? 'sim-step-in' : ''
                  }`}
                >
                  {index < steps.length - 1 && (
                    <div
                      className={`absolute left-[19px] top-10 bottom-0 w-0.5 transition-all duration-700 ${
                        step.status === 'complete' ? sim.connectorActive : sim.connector
                      }`}
                    />
                  )}
                  <div
                    className={`relative z-[1] w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 transition-all duration-300 ${iconShell(step.status)}`}
                  >
                    {step.status === 'running' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : step.status === 'complete' ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      step.icon
                    )}
                  </div>
                  <div className={`flex-1 min-w-0 rounded-xl border px-3 py-2.5 transition-all duration-300 ${stepShell(step.status)}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-xs font-semibold leading-tight ${colors.text.primary}`}>{step.title}</p>
                      {step.status === 'complete' && (
                        <span className={`text-[9px] font-mono tabular-nums ${colors.text.muted}`}>
                          {step.durationMs}ms
                        </span>
                      )}
                      {step.status === 'running' && (
                        <span className="text-[9px] font-medium text-violet-500 animate-pulse">live</span>
                      )}
                    </div>
                    <AiStepContent step={step} colors={colors} sim={sim} />
                  </div>
                </div>
              ))}
            </div>

            {finished && (
              <div className="mt-4 space-y-3 sim-step-in">
                <div className={`rounded-xl border px-4 py-3 ${sim.resultBox}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                      Routing decision
                    </p>
                  </div>
                  <p className={`text-sm font-medium ${colors.text.primary}`}>{getResultSummary(context)}</p>
                  <p className={`text-[10px] mt-1 font-mono ${colors.text.muted}`}>
                    {context.intentCode} · {context.classification} · {(context.confidence * 100).toFixed(0)}%
                  </p>
                </div>

                <div className={`rounded-xl border px-4 py-3 ${sim.chatBubble}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-6 h-6 rounded-lg border flex items-center justify-center ${sim.headerIcon}`}>
                      <Bot className="w-3.5 h-3.5" />
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-wide text-violet-500`}>
                      Assistant preview
                    </span>
                  </div>
                  <p className={`text-[12px] leading-relaxed ${colors.text.primary}`}>{getAiResponse(context)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={`px-5 py-3 border-t ${colors.border} flex justify-end gap-2 shrink-0`}>
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 text-xs rounded-lg border font-medium ${
                theme === 'light'
                  ? 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  : 'border-neutral-600 text-neutral-300 hover:bg-neutral-800'
              }`}
            >
              {finished ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default IntentAnalysisSimulation;
